import React, { useEffect, useRef } from "react";

// Fundo animado "grid + partículas": grade sutil de pontos que brilham perto
// do ponteiro, mais partículas soltas conectadas por linhas quando próximas.
// Adaptado do mockup fusao-grid-particulas.html (efeito "Rede Viva") pras
// cores reais do tema (var(--accent)/var(--accent2), ver styles/global.css)
// em vez do violeta/ciano originais do mockup, pra manter a identidade visual
// já usada na LandingPage (blobs azuis) e no resto do app.
//
// Uso: <ParticleGridBackground /> como primeiro filho de um container com
// position:relative (ou dentro de uma section com overflow:hidden). O canvas
// fica fixed + pointer-events:none, então nunca atrapalha cliques/scroll.
export default function ParticleGridBackground({ corBase = "37,99,235", corLinha = "59,130,246" }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const spacing = 64;
    let w, h, particles = [];
    let mouse = { x: null, y: null };
    let target = { x: null, y: null };
    let raf = null;

    function pcount() {
      return w < 640 ? 30 : w < 1100 ? 50 : 75;
    }

    function resize() {
      w = canvas.width = window.innerWidth;
      h = canvas.height = window.innerHeight;
      particles = [];
      const n = pcount();
      for (let i = 0; i < n; i++) {
        particles.push({
          x: Math.random() * w,
          y: Math.random() * h,
          vx: (Math.random() - 0.5) * 0.35,
          vy: (Math.random() - 0.5) * 0.35,
          r: 1 + Math.random() * 1.6,
        });
      }
    }

    function onPointerMove(e) {
      target.x = e.clientX;
      target.y = e.clientY;
    }
    function onPointerLeave() {
      target.x = null;
      target.y = null;
    }

    function draw() {
      ctx.clearRect(0, 0, w, h);

      ctx.strokeStyle = "rgba(255,255,255,0.03)";
      ctx.lineWidth = 1;
      for (let x = 0; x < w; x += spacing) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke(); }
      for (let y = 0; y < h; y += spacing) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke(); }

      if (mouse.x !== null) {
        const grad = ctx.createRadialGradient(mouse.x, mouse.y, 0, mouse.x, mouse.y, 260);
        grad.addColorStop(0, `rgba(${corBase},0.12)`);
        grad.addColorStop(0.6, `rgba(${corLinha},0.05)`);
        grad.addColorStop(1, `rgba(${corLinha},0)`);
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, w, h);
      }

      for (let x = 0; x < w; x += spacing) {
        for (let y = 0; y < h; y += spacing) {
          let a = 0.045;
          if (mouse.x !== null) {
            const d = Math.hypot(x - mouse.x, y - mouse.y);
            if (d < 220) a = 0.045 + (1 - d / 220) * 0.4;
          }
          ctx.fillStyle = `rgba(${corBase},${a})`;
          ctx.beginPath(); ctx.arc(x, y, 1.5, 0, Math.PI * 2); ctx.fill();
        }
      }

      particles.forEach((p) => {
        if (!reduced) {
          if (mouse.x !== null) {
            const dx = mouse.x - p.x, dy = mouse.y - p.y, d = Math.hypot(dx, dy);
            if (d < 180 && d > 0) { const f = (1 - d / 180) * 0.03; p.vx += (dx / d) * f; p.vy += (dy / d) * f; }
          }
          p.vx *= 0.985; p.vy *= 0.985;
          p.x += p.vx; p.y += p.vy;
          if (p.x < 0) p.x = w; if (p.x > w) p.x = 0;
          if (p.y < 0) p.y = h; if (p.y > h) p.y = 0;
        }
        ctx.beginPath();
        ctx.fillStyle = "rgba(255,255,255,0.45)";
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      });

      if (!reduced) {
        for (let i = 0; i < particles.length; i++) {
          for (let j = i + 1; j < particles.length; j++) {
            const dx = particles[i].x - particles[j].x, dy = particles[i].y - particles[j].y, d = Math.hypot(dx, dy);
            if (d < 130) {
              ctx.strokeStyle = `rgba(${corLinha},${(1 - d / 130) * 0.15})`;
              ctx.lineWidth = 1;
              ctx.beginPath(); ctx.moveTo(particles[i].x, particles[i].y); ctx.lineTo(particles[j].x, particles[j].y); ctx.stroke();
            }
          }
        }
      }

      if (!reduced && target.x !== null) {
        mouse.x = (mouse.x ?? target.x) + (target.x - (mouse.x ?? target.x)) * 0.1;
        mouse.y = (mouse.y ?? target.y) + (target.y - (mouse.y ?? target.y)) * 0.1;
      } else {
        mouse.x = target.x; mouse.y = target.y;
      }

      raf = requestAnimationFrame(draw);
    }

    resize();
    window.addEventListener("resize", resize);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerleave", onPointerLeave);

    if (reduced) {
      draw();
    } else {
      raf = requestAnimationFrame(draw);
    }

    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerleave", onPointerLeave);
    };
  }, [corBase, corLinha]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{ position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none" }}
    />
  );
}
